import HttpError from "./HttpError";

export interface HttpClientOptions {
  baseUrl: string;
  token?: string | null;
  defaultHeaders?: HeadersInit;
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  query?: Record<string, string | number | boolean | null | undefined>;
  body?: unknown;
}

export class HttpClient {
  private readonly baseUrl: string;
  private readonly defaultHeaders: HeadersInit;

  constructor({
    baseUrl,
    defaultHeaders = {},
  }: HttpClientOptions) {
    this.baseUrl = baseUrl.replace(/\/$/, "");

    this.defaultHeaders = {
      Accept: "application/json",
      ...defaultHeaders,
    };
  }


  private buildUrl(
    path: string,
    query?: RequestOptions["query"]
  ): string {
    const url = new URL(`${this.baseUrl}${path}`);

    if (query) {
      for (const [key, value] of Object.entries(query)) {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      }
    }

    return url.toString();
  }

  public async request<T = unknown>(
    path: string,
    options: RequestOptions = {}
  ): Promise<T> {
    const {
      method = "GET",
      headers = {},
      body,
      query,
      signal,
      ...fetchOptions
    } = options;

    const finalHeaders = new Headers(this.defaultHeaders);

    new Headers(headers).forEach((value, key) => {
      finalHeaders.set(key, value);
    });


    let requestBody: BodyInit | undefined;

    if (body !== undefined && body !== null) {
      if (body instanceof FormData) {
        requestBody = body;
      } else {
        finalHeaders.set("Content-Type", "application/json");
        requestBody = JSON.stringify(body);
      }
    }

    const url = this.buildUrl(path, query)
    const response = await fetch(url, {
      ...fetchOptions,
      method,
      headers: finalHeaders,
      body: requestBody,
      signal,
    });

    const contentType = response.headers.get("content-type") ?? "";

    let data: unknown = null;

    console.log(`[HTTPClient] [${response.status}] ${method} ${url}`)


    if (response.status !== 204) {
      if (contentType.includes("application/json")) {
        data = await response.json();
      } else if (contentType.startsWith("text/")) {
        data = await response.text();
      } else {
        data = await response.arrayBuffer();
      }
    }

    if (!response.ok) {
      throw new HttpError(
        response.status,
        response.statusText,
        data
      );
    }

    return data as T;
  }

  public get<T = any>(
    path: string,
    options: Omit<RequestOptions, "method" | "body"> = {}
  ): Promise<T> {
    return this.request<T>(path, {
      ...options,
      method: "GET",
    });
  }

  public post<TResponse = unknown, TRequest = unknown>(
    path: string,
    body?: TRequest,
    options: Omit<RequestOptions, "method" | "body"> = {}
  ): Promise<TResponse> {
    return this.request<TResponse>(path, {
      ...options,
      method: "POST",
      body,
    });
  }

  public put<T = unknown>(
    path: string,
    body?: unknown,
    options: Omit<RequestOptions, "method" | "body"> = {}
  ): Promise<T> {
    return this.request<T>(path, {
      ...options,
      method: "PUT",
      body,
    });
  }

  public patch<T = unknown>(
    path: string,
    body?: unknown,
    options: Omit<RequestOptions, "method" | "body"> = {}
  ): Promise<T> {
    return this.request<T>(path, {
      ...options,
      method: "PATCH",
      body,
    });
  }

  public delete<T = unknown>(
    path: string,
    options: Omit<RequestOptions, "method" | "body"> = {}
  ): Promise<T> {
    return this.request<T>(path, {
      ...options,
      method: "DELETE",
    });
  }
}